import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Avatar,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Icon,
  Grid,
  GridItem,
  useColorMode,
  Divider,
  IconButton,
  useToast,
  Tooltip,
} from '@chakra-ui/react';
import { FiClock, FiDollarSign, FiCalendar, FiVideo, FiMapPin, FiUser, FiLink, FiCheck } from 'react-icons/fi';
import { format, startOfWeek, addDays, isSameDay, parseISO } from 'date-fns';
import { SunshineBackground } from './SunshineBackground';
import { getColor } from '../../../brandConfig';
import {
  PUBLIC_CALENDAR_QUERY,
  PUBLIC_EVENT_TYPES_QUERY,
  AVAILABLE_TIME_SLOTS_QUERY,
} from '../queries';

// ==================== TYPES ====================

interface PublicCalendar {
  id: string;
  name: string;
  description?: string;
  color: string;
  bookingPageSlug: string;
  bookingPageTitle?: string;
  bookingPageDescription?: string;
  bookingPageLogo?: string;
  bookingPageProfilePhoto?: string;
}

interface BookableEventType {
  id: string;
  name: string;
  description?: string;
  durationMinutes: number;
  isPaid: boolean;
  price?: number;
  currency?: string;
  locationType: string;
  location?: string;
  meetingLink?: string;
  isActive: boolean;
}

interface TimeSlot {
  startTime: string;
  endTime: string;
  available: boolean;
}

// ==================== COMPONENT ====================

export const PublicBookingPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [selectedEventType, setSelectedEventType] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTime, setSelectedTime] = useState<TimeSlot | null>(null);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const { colorMode } = useColorMode();
  const toast = useToast();

  // Use brandConfig colors
  const bg = getColor("background.main", colorMode);
  const cardGradientBg = colorMode === 'light'
    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(248, 248, 248, 0.65) 50%, rgba(255, 255, 255, 0.7) 100%)'
    : 'linear-gradient(135deg, rgba(20, 20, 20, 0.7) 0%, rgba(30, 30, 30, 0.5) 50%, rgba(20, 20, 20, 0.7) 100%)';
  const cardBorder = colorMode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)';
  const textPrimary = getColor(colorMode === 'light' ? 'text.primary' : 'text.primaryDark', colorMode);
  const textSecondary = getColor(colorMode === 'light' ? 'text.secondary' : 'text.secondaryDark', colorMode);
  const highlightBg = colorMode === 'light' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.2)';
  const primaryBlue = getColor('primary', colorMode);

  // Booking confirmation card colors
  const confirmationCardBg = colorMode === 'light'
    ? `linear-gradient(135deg, ${getColor('primary', colorMode)} 0%, ${getColor('primaryHover', colorMode)} 100%)`
    : 'linear-gradient(135deg, #1a365d 0%, #2c5282 100%)'; // Dark blue gradient for dark theme
  const confirmationCardBorder = colorMode === 'light' ? getColor('primary', colorMode) : '#2d3748';
  const confirmationCardTextColor = 'white';
  const confirmationInnerBg = colorMode === 'light'
    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 100%)'
    : 'linear-gradient(135deg, rgba(45, 55, 72, 0.95) 0%, rgba(26, 32, 44, 0.85) 100%)'; // Dark gray gradient
  const confirmationInnerBorder = colorMode === 'light' ? 'whiteAlpha.400' : 'whiteAlpha.200';
  const confirmationIconColor = colorMode === 'light' ? getColor('primary', colorMode) : '#63b3ed';
  const confirmationTextColor = colorMode === 'light' ? 'gray.800' : 'gray.100';
  const confirmationDividerColor = colorMode === 'light' ? 'whiteAlpha.500' : 'whiteAlpha.300';

  // Query for calendar details
  const { data: calendarData, loading: calendarLoading, error: calendarError } = useQuery(
    PUBLIC_CALENDAR_QUERY,
    {
      variables: { slug },
      skip: !slug,
    }
  );

  // Query for event types
  const { data: eventTypesData, loading: eventTypesLoading, error: eventTypesError } = useQuery(
    PUBLIC_EVENT_TYPES_QUERY,
    {
      variables: { calendarSlug: slug },
      skip: !slug,
    }
  );

  // Utility function to generate slug from event type name (matching backend logic)
  const generateEventTypeSlug = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  // Auto-select event type from query parameter or select first event type
  useEffect(() => {
    if (!eventTypesData?.publicBookableEventTypes || hasAutoSelected) return;

    const eventTypes = eventTypesData.publicBookableEventTypes as BookableEventType[];
    if (eventTypes.length === 0) return;

    // Check for query parameters
    const eventTypeSlug = searchParams.get('eventType');
    const eventTypeId = searchParams.get('id');

    if (eventTypeId) {
      // Direct ID match (most reliable)
      const matchedEvent = eventTypes.find(et => et.id === eventTypeId);
      if (matchedEvent) {
        console.log('🎯 Auto-selected event type by ID:', matchedEvent.name);
        setSelectedEventType(matchedEvent.id);
        setHasAutoSelected(true);
        return;
      }
    }

    if (eventTypeSlug) {
      // Try to match by slug
      const matchedEvent = eventTypes.find(
        et => generateEventTypeSlug(et.name) === eventTypeSlug
      );
      if (matchedEvent) {
        console.log('🎯 Auto-selected event type by slug:', matchedEvent.name);
        setSelectedEventType(matchedEvent.id);
        setHasAutoSelected(true);
        return;
      }
    }

    // No query parameter or no match - auto-select first event type
    console.log('📋 Auto-selecting first event type:', eventTypes[0].name);
    setSelectedEventType(eventTypes[0].id);
    setHasAutoSelected(true);
  }, [eventTypesData, hasAutoSelected, searchParams]);

  // Debug logging
  useEffect(() => {
    console.log('📋 Event Types Query Status:');
    console.log('   Loading:', eventTypesLoading);
    console.log('   Error:', eventTypesError);
    console.log('   Data:', eventTypesData);
    console.log('   Slug:', slug);
    console.log('   Query Params:', {
      eventType: searchParams.get('eventType'),
      id: searchParams.get('id'),
    });
  }, [eventTypesLoading, eventTypesError, eventTypesData, slug, searchParams]);

  // Query for available time slots (only when event type selected)
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 }); // Monday
  const weekEnd = addDays(weekStart, 6);

  const { data: timeSlotsData, loading: timeSlotsLoading, refetch: refetchTimeSlots } = useQuery(
    AVAILABLE_TIME_SLOTS_QUERY,
    {
      variables: {
        calendarId: calendarData?.publicCalendar?.id,
        eventTypeId: selectedEventType,
        startDate: weekStart.toISOString(),
        endDate: weekEnd.toISOString(),
      },
      skip: !calendarData?.publicCalendar?.id || !selectedEventType,
      fetchPolicy: 'network-only', // Always fetch fresh data, don't use cache
    }
  );

  // Refetch time slots when event type changes
  useEffect(() => {
    if (selectedEventType && calendarData?.publicCalendar?.id) {
      console.log('🔄 Event type changed, refetching time slots for:', selectedEventType);
      refetchTimeSlots();
    }
  }, [selectedEventType, calendarData?.publicCalendar?.id, refetchTimeSlots]);

  // ==================== HANDLERS ====================

  const handleEventTypeSelect = (eventTypeId: string) => {
    console.log('📋 Selecting event type:', eventTypeId);
    setSelectedEventType(eventTypeId);
    setSelectedTime(null); // Clear selected time when changing event type
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setSelectedTime(null);
  };

  const handleTimeSelect = (timeSlot: TimeSlot) => {
    setSelectedTime(timeSlot);
  };

  const handleBooking = () => {
    if (!selectedEventType || !selectedTime) return;

    // Navigate to booking form with selected details
    navigate(`/book/${slug}/confirm`, {
      state: {
        eventTypeId: selectedEventType,
        startTime: selectedTime.startTime,
        endTime: selectedTime.endTime,
      },
    });
  };

  const generateEventTypeSlugForLink = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  const copyBookingLink = async () => {
    if (!selectedEventType || !selectedEventTypeDetails || !slug) return;

    const eventTypeSlug = generateEventTypeSlugForLink(selectedEventTypeDetails.name);
    const baseUrl = window.location.origin;
    const directLink = `${baseUrl}/book/${slug}?eventType=${eventTypeSlug}&id=${selectedEventType}`;

    try {
      await navigator.clipboard.writeText(directLink);
      setLinkCopied(true);
      toast({
        title: 'Link copied!',
        description: 'Direct booking link for this event type copied to clipboard',
        status: 'success',
        duration: 2000,
      });

      setTimeout(() => {
        setLinkCopied(false);
      }, 2000);
    } catch (err) {
      toast({
        title: 'Failed to copy',
        description: 'Please try again',
        status: 'error',
        duration: 3000,
      });
    }
  };

  // ==================== LOADING & ERROR STATES ====================

  if (calendarLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <VStack spacing={4}>
          <Spinner size="xl" />
          <Text>Loading booking page...</Text>
        </VStack>
      </Container>
    );
  }

  if (calendarError || !calendarData?.publicCalendar) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error" variant="left-accent">
          <AlertIcon />
          <Box>
            <AlertTitle>Calendar Not Found</AlertTitle>
            <AlertDescription>
              This booking page doesn't exist or is no longer accepting bookings.
            </AlertDescription>
          </Box>
        </Alert>
      </Container>
    );
  }

  const calendar = calendarData.publicCalendar as PublicCalendar;
  const eventTypes = (eventTypesData?.publicBookableEventTypes || []) as BookableEventType[];
  const timeSlots = (timeSlotsData?.availableTimeSlots || []) as TimeSlot[];

  // Group time slots by day
  const slotsByDay: Record<string, TimeSlot[]> = {};
  timeSlots.forEach((slot) => {
    const date = format(parseISO(slot.startTime), 'yyyy-MM-dd');
    if (!slotsByDay[date]) {
      slotsByDay[date] = [];
    }
    slotsByDay[date].push(slot);
  });

  // Generate week days
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Find selected event type details
  const selectedEventTypeDetails = eventTypes.find((et) => et.id === selectedEventType);

  // ==================== RENDER ====================

  return (
      <Box minH="100vh" position="relative" bg={bg}>
        <SunshineBackground />
        <Box position="relative" zIndex={1}>
      {/* Header */}
      <Box bg={cardGradientBg} borderBottom="1px" borderColor={cardBorder} py={{ base: 4, md: 8 }} backdropFilter="blur(10px)">
        <Container maxW="container.xl">
          <VStack spacing={4} align="start">
            <HStack spacing={4} align="start" w="full" flexWrap={{ base: 'wrap', sm: 'nowrap' }}>
              <Avatar
                size={{ base: 'md', md: 'lg' }}
                name={calendar.bookingPageTitle || calendar.name}
                bg={calendar.color}
                color="white"
                src={calendar.bookingPageProfilePhoto || calendar.bookingPageLogo}
              />
              <VStack align="start" spacing={1} flex={1}>
                <Heading size={{ base: 'md', md: 'lg' }} color={textPrimary}>
                  {calendar.bookingPageTitle || calendar.name}
                </Heading>
                <Text color={textSecondary} fontSize={{ base: 'sm', md: 'md' }}>
                  {calendar.bookingPageDescription || calendar.description}
                </Text>
              </VStack>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxW="container.xl" py={8}>
        <Grid templateColumns={{ base: '1fr', lg: '1fr 2fr' }} gap={8}>
          {/* Sidebar - Event Type Selection */}
          <GridItem>
            <Card bg={cardGradientBg} backdropFilter="blur(10px)" borderColor={cardBorder} borderWidth="1px">
              <CardHeader>
                <Heading size="md" color={textPrimary}>Select Event Type</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={3} align="stretch">
                  {eventTypesLoading ? (
                    <Spinner />
                  ) : eventTypes.length === 0 ? (
                    <Alert status="info">
                      <AlertIcon />
                      <Text fontSize="sm">No event types available</Text>
                    </Alert>
                  ) : (
                    eventTypes.map((eventType) => (
                      <Card
                        key={eventType.id}
                        variant={selectedEventType === eventType.id ? 'filled' : 'outline'}
                        cursor="pointer"
                        onClick={() => handleEventTypeSelect(eventType.id)}
                        borderColor={
                          selectedEventType === eventType.id
                            ? 'blue.500'
                            : cardBorder
                        }
                        borderWidth={selectedEventType === eventType.id ? '2px' : '1px'}
                        _hover={{ shadow: 'md' }}
                        transition="all 0.2s"
                      >
                        <CardBody p={{ base: 3, md: 4 }}>
                          <VStack align="start" spacing={2}>
                            <Heading size={{ base: 'xs', md: 'sm' }}>{eventType.name}</Heading>
                            {eventType.description && (
                              <Text fontSize="xs" color="gray.600" noOfLines={2}>
                                {eventType.description}
                              </Text>
                            )}
                            <HStack spacing={{ base: 2, md: 4 }} fontSize="xs" color="gray.600" flexWrap="wrap">
                              <HStack>
                                <Icon as={FiClock} />
                                <Text>{eventType.durationMinutes} min</Text>
                              </HStack>
                              {eventType.isPaid && (
                                <HStack>
                                  <Icon as={FiDollarSign} />
                                  <Text>
                                    {eventType.price} {eventType.currency}
                                  </Text>
                                </HStack>
                              )}
                              <HStack>
                                <Icon as={eventType.locationType === 'Virtual' ? FiVideo : FiMapPin} />
                                <Text>{eventType.locationType}</Text>
                              </HStack>
                            </HStack>
                          </VStack>
                        </CardBody>
                      </Card>
                    ))
                  )}
                </VStack>
              </CardBody>
            </Card>
          </GridItem>

          {/* Main Area - Week View & Time Selection */}
          <GridItem>
            {!selectedEventType ? (
              <Card bg={cardGradientBg} h="full" borderColor={cardBorder} borderWidth="1px" backdropFilter="blur(10px)">
                <CardBody>
                  <VStack spacing={4} justify="center" h="full">
                    <Icon as={FiCalendar} boxSize={16} color="gray.400" />
                    <Heading size="md" color="gray.600">
                      Select an event type to see availability
                    </Heading>
                    <Text color="gray.500" textAlign="center">
                      Choose an event type from the left to view available time slots
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            ) : (
              <VStack spacing={6} align="stretch">
                {/* Selected Event Type Info */}
                {selectedEventTypeDetails && (
                  <Card bg={cardGradientBg} backdropFilter="blur(10px)" borderColor={cardBorder} borderWidth="1px">
                    <CardBody>
                      <HStack justify="space-between">
                        <VStack align="start" spacing={1} flex={1}>
                          <Heading size="sm">{selectedEventTypeDetails.name}</Heading>
                          <HStack spacing={4} fontSize="sm" color="gray.600">
                            <HStack>
                              <Icon as={FiClock} />
                              <Text>{selectedEventTypeDetails.durationMinutes} minutes</Text>
                            </HStack>
                            {selectedEventTypeDetails.isPaid && (
                              <Badge colorScheme="green">
                                {selectedEventTypeDetails.price} {selectedEventTypeDetails.currency}
                              </Badge>
                            )}
                          </HStack>
                        </VStack>
                        <HStack spacing={2}>
                          <Tooltip label="Copy direct link to this event type" placement="top">
                            <IconButton
                              aria-label="Copy booking link"
                              icon={linkCopied ? <Icon as={FiCheck} /> : <Icon as={FiLink} />}
                              size="sm"
                              colorScheme={linkCopied ? "green" : "blue"}
                              variant="ghost"
                              onClick={copyBookingLink}
                            />
                          </Tooltip>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedEventType(null)}
                          >
                            Change
                          </Button>
                        </HStack>
                      </HStack>
                    </CardBody>
                  </Card>
                )}

                {/* Week Navigation */}
                <Card bg={cardGradientBg} backdropFilter="blur(10px)" borderColor={cardBorder} borderWidth="1px">
                  <CardBody p={{ base: 3, md: 4 }}>
                    <VStack spacing={3} mb={4}>
                      <Heading size={{ base: 'xs', md: 'sm' }} textAlign="center">
                        {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
                      </Heading>
                      <HStack justify="center" spacing={2} w="full">
                        <Button
                          size={{ base: 'sm', md: 'md' }}
                          onClick={() => handleDateSelect(addDays(weekStart, -7))}
                          flex={1}
                          maxW={{ base: '140px', md: '160px' }}
                        >
                          Previous
                        </Button>
                        <Button
                          size={{ base: 'sm', md: 'md' }}
                          onClick={() => handleDateSelect(addDays(weekStart, 7))}
                          flex={1}
                          maxW={{ base: '140px', md: '160px' }}
                        >
                          Next
                        </Button>
                      </HStack>
                    </VStack>

                    <Divider mb={4} />

                    {/* Week Grid */}
                    {timeSlotsLoading ? (
                      <VStack py={8}>
                        <Spinner />
                        <Text>Loading availability...</Text>
                      </VStack>
                    ) : (
                      <Grid templateColumns={{ base: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(7, 1fr)' }} gap={2}>
                        {weekDays.map((day) => {
                          const dateKey = format(day, 'yyyy-MM-dd');
                          const daySlots = slotsByDay[dateKey] || [];
                          const isToday = isSameDay(day, new Date());
                          const isSelected = isSameDay(day, selectedDate);

                          return (
                            <GridItem key={dateKey}>
                              <VStack
                                spacing={2}
                                p={3}
                                borderRadius="md"
                                bg={isSelected ? highlightBg : 'transparent'}
                                borderWidth={isToday ? '2px' : '1px'}
                                borderColor={isToday ? primaryBlue : cardBorder}
                              >
                                <Text fontWeight="bold" fontSize="xs" color={textPrimary}>
                                  {format(day, 'EEE')}
                                </Text>
                                <Text fontSize="lg" fontWeight="bold" color={textPrimary}>
                                  {format(day, 'd')}
                                </Text>
                                <Divider />
                                {daySlots.length === 0 ? (
                                  <Text fontSize="xs" color="gray.500">
                                    No slots
                                  </Text>
                                ) : (
                                  <VStack
                                    spacing={1}
                                    w="full"
                                    maxH="280px"
                                    overflowY="auto"
                                    css={{
                                      '&::-webkit-scrollbar': {
                                        width: '4px',
                                      },
                                      '&::-webkit-scrollbar-track': {
                                        background: 'transparent',
                                      },
                                      '&::-webkit-scrollbar-thumb': {
                                        background: '#CBD5E0',
                                        borderRadius: '4px',
                                      },
                                      '&::-webkit-scrollbar-thumb:hover': {
                                        background: '#A0AEC0',
                                      },
                                    }}
                                  >
                                    {daySlots.map((slot, idx) => (
                                      <Button
                                        key={idx}
                                        size="xs"
                                        w="full"
                                        variant={
                                          selectedTime?.startTime === slot.startTime
                                            ? 'solid'
                                            : 'outline'
                                        }
                                        colorScheme={
                                          selectedTime?.startTime === slot.startTime
                                            ? 'blue'
                                            : 'gray'
                                        }
                                        onClick={() => {
                                          handleDateSelect(day);
                                          handleTimeSelect(slot);
                                        }}
                                        isDisabled={!slot.available}
                                      >
                                        {format(parseISO(slot.startTime), 'HH:mm')}
                                      </Button>
                                    ))}
                                  </VStack>
                                )}
                              </VStack>
                            </GridItem>
                          );
                        })}
                      </Grid>
                    )}
                  </CardBody>
                </Card>

                {/* Booking Action - Prominent CTA */}
                {selectedTime && (
                  <Card
                    bg={confirmationCardBg}
                    borderColor={confirmationCardBorder}
                    borderWidth="2px"
                    shadow="2xl"
                    _hover={{ shadow: '3xl', transform: 'translateY(-4px)' }}
                    transition="all 0.3s"
                  >
                    <CardBody py={8}>
                      <VStack spacing={6}>
                        <VStack spacing={2}>
                          <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color={confirmationCardTextColor} textAlign="center">
                            You've Selected
                          </Text>
                          <Divider borderColor={confirmationDividerColor} />
                        </VStack>

                        <VStack spacing={3} bg={confirmationInnerBg} p={{ base: 4, md: 6 }} borderRadius="xl" w="full" shadow="xl" backdropFilter="blur(10px)" borderWidth={1} borderColor={confirmationInnerBorder}>
                          <HStack spacing={3} flexWrap="wrap" justify="center">
                            <Icon as={FiCalendar} boxSize={{ base: 5, md: 6 }} color={confirmationIconColor} />
                            <Text fontSize={{ base: 'md', md: 'xl' }} fontWeight="bold" color={confirmationTextColor} textAlign="center">
                              {format(parseISO(selectedTime.startTime), 'EEEE, MMMM d, yyyy')}
                            </Text>
                          </HStack>
                          <HStack spacing={3} flexWrap="wrap" justify="center">
                            <Icon as={FiClock} boxSize={{ base: 5, md: 6 }} color={confirmationIconColor} />
                            <Text fontSize={{ base: 'md', md: 'xl' }} fontWeight="bold" color={confirmationTextColor} textAlign="center">
                              {format(parseISO(selectedTime.startTime), 'h:mm a')} - {format(parseISO(selectedTime.endTime), 'h:mm a')}
                            </Text>
                          </HStack>
                          {selectedEventTypeDetails && (
                            <HStack spacing={3} pt={2} flexWrap="wrap" justify="center">
                              <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                                {selectedEventTypeDetails.name}
                              </Badge>
                              {selectedEventTypeDetails.isPaid && (
                                <Badge colorScheme="green" fontSize="md" px={3} py={1}>
                                  {selectedEventTypeDetails.price} {selectedEventTypeDetails.currency}
                                </Badge>
                              )}
                            </HStack>
                          )}
                        </VStack>

                        <Button
                          bg={colorMode === 'light' ? 'white' : 'gray.700'}
                          color={colorMode === 'light' ? getColor('primary', colorMode) : 'blue.300'}
                          size={{ base: 'lg', md: 'xl' }}
                          fontSize={{ base: 'lg', md: 'xl' }}
                          fontWeight="bold"
                          px={{ base: 6, md: 12 }}
                          py={{ base: 6, md: 8 }}
                          h="auto"
                          w="full"
                          onClick={handleBooking}
                          leftIcon={<Icon as={FiUser} boxSize={{ base: 5, md: 6 }} />}
                          _hover={{
                            bg: colorMode === 'light' ? 'blue.50' : 'gray.600',
                            transform: 'scale(1.05)',
                            shadow: '2xl'
                          }}
                          _active={{
                            transform: 'scale(0.98)'
                          }}
                          transition="all 0.2s"
                          shadow="lg"
                        >
                          Continue to Book
                        </Button>
                      </VStack>
                    </CardBody>
                  </Card>
                )}
              </VStack>
            )}
          </GridItem>
        </Grid>
      </Container>
        </Box>
      </Box>
  );
};

export default PublicBookingPage;
